import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const SettingsContext = createContext(null);

// Default settings
const defaultSettings = {
  // General
  companyName: 'Office Suite',
  companyEmail: 'info@officesuite.com',
  companyPhone: '+234 800 123 4567',
  companyAddress: 'Lagos, Nigeria',
  currency: 'NGN',
  currencySymbol: '₦',
  timezone: 'Africa/Lagos',
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
  language: 'en',
  fiscalYearStart: 'january',
  
  // Appearance
  theme: 'light',
  accentColor: '#4A7C59',
  compactMode: false,
  showAnimations: true,
  sidebarCollapsed: false,
  
  // Notifications
  emailNotifications: true,
  pushNotifications: true,
  soundEnabled: true,
  notifyApprovals: true,
  notifyLowStock: true,
  notifyLeaveRequests: true,
  notifyPayroll: true,
  notifyPerformance: true,
  notifySystem: true,
  digestFrequency: 'daily',
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '07:00',
  
  // Inventory
  lowStockThreshold: 10,
  criticalStockThreshold: 5,
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);

  // Apply theme settings to the document
  const applyTheme = useCallback((settingsData) => {
    const root = document.documentElement;
    
    // Apply accent color as CSS variable
    if (settingsData.accentColor) {
      root.style.setProperty('--accent-color', settingsData.accentColor);
      // Calculate a darker shade for hover states
      const darkerColor = adjustColorBrightness(settingsData.accentColor, -20);
      root.style.setProperty('--accent-color-dark', darkerColor);
      // Calculate a lighter shade for backgrounds
      const lighterColor = adjustColorBrightness(settingsData.accentColor, 40);
      root.style.setProperty('--accent-color-light', lighterColor + '20'); // With transparency
    }
    
    // Apply theme (light/dark)
    if (settingsData.theme === 'dark') {
      root.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      root.classList.remove('dark');
      document.body.classList.remove('dark');
    }
    
    // Apply compact mode
    if (settingsData.compactMode) {
      root.classList.add('compact');
    } else {
      root.classList.remove('compact');
    }
    
    // Apply animations preference
    if (!settingsData.showAnimations) {
      root.classList.add('no-animations');
    } else {
      root.classList.remove('no-animations');
    }
  }, []);

  // Load settings from backend
  const loadSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/settings`);
      if (response.data) {
        const loadedSettings = { ...defaultSettings, ...response.data };
        setSettings(loadedSettings);
        applyTheme(loadedSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Apply default theme on error
      applyTheme(defaultSettings);
    } finally {
      setLoading(false);
    }
  }, [applyTheme]);

  // Save settings to backend
  const saveSettings = async (newSettings) => {
    try {
      await axios.post(`${API}/settings`, newSettings);
      setSettings(newSettings);
      applyTheme(newSettings);
      return { success: true };
    } catch (error) {
      console.error('Error saving settings:', error);
      return { success: false, error: error.message };
    }
  };

  // Update a single setting
  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applyTheme(newSettings);
  };

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Re-apply theme whenever settings change
  useEffect(() => {
    applyTheme(settings);
  }, [settings, applyTheme]);

  const value = {
    settings,
    setSettings,
    saveSettings,
    updateSetting,
    loadSettings,
    loading,
    defaultSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

// Helper function to adjust color brightness
function adjustColorBrightness(hex, percent) {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Adjust brightness
  r = Math.max(0, Math.min(255, r + (r * percent / 100)));
  g = Math.max(0, Math.min(255, g + (g * percent / 100)));
  b = Math.max(0, Math.min(255, b + (b * percent / 100)));
  
  // Convert back to hex
  const rHex = Math.round(r).toString(16).padStart(2, '0');
  const gHex = Math.round(g).toString(16).padStart(2, '0');
  const bHex = Math.round(b).toString(16).padStart(2, '0');
  
  return `#${rHex}${gHex}${bHex}`;
}

export default SettingsContext;
